import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type {
	AiStreamingOrchestratorPlugin,
	StreamingCommand,
	StreamingError,
} from '@atlassian/editor-plugin-ai-streaming-orchestrator';
import type { MultiFormatContentType } from '@atlassian/editor-plugin-ai-streaming-orchestrator/types';

import type { ContentLocation } from '../types/content';
import { log } from '../utils/aifc-logging';

import { getEnvelopeContentType, type ToolCallV2Envelope } from './partial-stream-parser-v2';
import {
	createToolProcessorChunkStrategyV2,
	type ToolProcessorChunkStrategyV2,
} from './tool-processor-chunk-strategies-v2';

/**
 * V2 per-tool-call processor for the HTML+ incremental streaming contract.
 *
 * Each {@link ToolCallV2Envelope} the parser emits for a given `id` is
 * routed to the same `ToolCallProcessorV2` instance, which translates the
 * envelope into orchestrator actions:
 *
 * - On the first envelope: build the {@link StreamingCommand} from the
 *   envelope's `name` + `arguments` and call `initAction`.
 * - On every envelope: pass `arguments.value` (the full snapshot) through the
 *   per-content-type V2 chunk strategy, then dispatch `replaceValue` to the
 *   orchestrator. This is the only place that calls `replaceValue` —
 *   `streamValue` (v1, append-style) is never used on the v2 path.
 * - On `status === 'complete'`: call `finishAction`.
 *
 * Errors building a command silently drop the envelope (matching v1
 * `createToolCallProcessor` behaviour).
 */
export class ToolCallProcessorV2 {
	private actionId: string | undefined;
	private command: StreamingCommand | undefined;
	private strategy: ToolProcessorChunkStrategyV2 | undefined;
	private finished: boolean = false;
	/**
	 * Set to `true` when `initAction` returns falsy. Once latched, subsequent
	 * `apply()` calls short-circuit so we don't re-init per chunk and leak
	 * un-finishable commands into the `StreamCommandQueue`.
	 */
	private initFailed: boolean = false;

	constructor(
		private editorApi: PublicPluginAPI<AiStreamingOrchestratorPlugin>,
		private contentLocation: ContentLocation,
	) {}

	/**
	 * Apply one v2 envelope to this tool call. Idempotent against repeat /
	 * stale sequences — the parser is expected to filter those out before
	 * calling, but we still guard against `finished` here.
	 */
	apply(envelope: ToolCallV2Envelope): void {
		if (this.finished || this.initFailed) {
			return;
		}

		// Lazy init on first envelope so the command is built from the most
		// recent metadata available. After `initAction`, subsequent envelopes
		// only stream a new snapshot.
		if (!this.actionId) {
			const command = buildCommandFromEnvelope(envelope);
			if (!command) {
				// Couldn't build a valid command: refuse to init (matching v1's
				// silent-drop) and latch `initFailed` so later envelopes don't retry.
				this.initFailed = true;
				log('V2: init failed (malformed envelope)', {
					streamingContract: 'v2',
					envelopeId: envelope.id,
				});
				return;
			}
			this.command = command;
			const actionId = this.editorApi?.aiStreamingOrchestrator?.actions.initAction(command, {
				hideLoadingScreen: this.contentLocation === 'staging-area',
			});
			if (!actionId) {
				// `initAction` returned falsy: latch `initFailed` so later `apply()`
				// calls short-circuit instead of queueing un-finishable commands.
				this.initFailed = true;
				log('V2: init failed (initAction returned falsy)', {
					streamingContract: 'v2',
					envelopeId: envelope.id,
				});
				return;
			}
			this.actionId = actionId;
			const contentType = getEnvelopeContentType(envelope);
			const schema = this.editorApi.core.sharedState.currentState()?.schema;
			this.strategy = createToolProcessorChunkStrategyV2(contentType, schema);
			log('V2: initialized streaming action', {
				streamingContract: 'v2',
				streamType: 'update',
				actionId,
				command,
				envelopeId: envelope.id,
			});
		}

		const isComplete = envelope.status === 'complete';
		const rawValue =
			typeof envelope.arguments?.value === 'string' ? (envelope.arguments.value as string) : '';

		if (rawValue) {
			const processed = this.strategy?.replaceSnapshot(rawValue, isComplete) ?? rawValue;
			this.editorApi.aiStreamingOrchestrator?.actions.replaceValue(this.actionId, processed);
			log('V2: replace snapshot', {
				streamingContract: 'v2',
				actionId: this.actionId,
				envelopeId: envelope.id,
				sequence: envelope.sequence,
				status: envelope.status,
			});
		} else if (this.command?.type === 'replaceDoc' && isComplete) {
			// Mirror v1's special case for replaceDoc with an empty value: the
			// orchestrator needs at least one streamValue/replaceValue call so it
			// can dispatch the empty-paragraph fallback rather than no-oping.
			this.editorApi.aiStreamingOrchestrator?.actions.replaceValue(this.actionId, '');
		}

		if (isComplete) {
			if (this.strategy?.getHadSchemaContentRejection() && this.actionId) {
				this.editorApi.aiStreamingOrchestrator?.actions.notifySchemaContentRejection(
					this.actionId,
					this.strategy.getSchemaRejectionDetails(),
				);
			}
			this.finish();
		}
	}

	/** Returns the envelope id this processor is bound to, or undefined if not yet initialised. */
	getEnvelopeId(): string | undefined {
		return this.boundEnvelopeId;
	}

	/**
	 * Returns `true` if this processor latched `initFailed`. The FIFO scheduler
	 * uses this to release the queue slot immediately rather than waiting for the
	 * parallel-queue timeout.
	 */
	getInitFailed(): boolean {
		return this.initFailed;
	}

	/**
	 * Finish the streaming action. When called with an `error`, propagate it via
	 * `finishAction(actionId, error)` so the orchestrator dispatches an error
	 * event instead of treating the last partial snapshot as a success.
	 */
	finish(error?: StreamingError, options?: { abortedDueToTimeout?: boolean }): void {
		if (this.finished) {
			return;
		}
		this.finished = true;
		if (this.actionId) {
			// On timeout force-finish, pass the bound tool-call id so the orchestrator
			// captures its working positions before ending the experience, enabling
			// resume at the mapped position.
			if (options?.abortedDueToTimeout) {
				this.editorApi?.aiStreamingOrchestrator?.actions.finishAction(this.actionId, error, {
					abortedToolCallId: this.boundEnvelopeId,
				});
			} else {
				// Preserve the original 2-arg call shape for the normal finish path.
				this.editorApi?.aiStreamingOrchestrator?.actions.finishAction(this.actionId, error);
			}
			log('V2: finished action', {
				streamingContract: 'v2',
				actionId: this.actionId,
				hasError: !!error,
				abortedDueToTimeout: !!options?.abortedDueToTimeout,
			});
		}
	}

	/** The streaming command this processor was initialised with, if any. */
	getCommand(): StreamingCommand | undefined {
		return this.command;
	}

	private boundEnvelopeId: string | undefined;

	/** Bind the envelope id (called by the wrapper on first apply). */
	bindEnvelopeId(id: string): void {
		this.boundEnvelopeId = id;
	}
}

/**
 * Translate a v2 envelope into a {@link StreamingCommand} the orchestrator
 * understands. Returns `undefined` when required fields are missing (the caller
 * silently drops the envelope, matching v1). The non-streamable cases mirror the
 * v1 mapping in `DocumentUpdateCommandProcessor.createToolCallProcessor` so both
 * paths build identical commands.
 */
function buildCommandFromEnvelope(envelope: ToolCallV2Envelope): StreamingCommand | undefined {
	const args = envelope.arguments ?? {};
	const contentType =
		typeof args.contentType === 'string' ? (args.contentType as MultiFormatContentType) : undefined;
	const localId = typeof args.localId === 'string' ? args.localId : '';
	const noStream = args.noStream === true ? { noStream: true as const } : undefined;

	switch (envelope.name) {
		case 'replaceDoc':
			return {
				type: 'replaceDoc',
				contentType,
				...(noStream ?? {}),
			};
		case 'replaceNode':
		case 'insertNodeBefore':
		case 'insertNodeAfter':
			if (!localId) {
				return undefined;
			}
			return {
				type: envelope.name,
				localId,
				contentType,
				...(noStream ?? {}),
			};
		case 'appendNode':
		case 'appendNodeToEnd':
			return {
				type: envelope.name,
				contentType,
			};
		// Non-streamable commands: no `value` to stream — fully described by
		// `arguments`. Required-field checks match v1; a missing field returns
		// `undefined` so the caller drops the tool call.
		case 'removeNode':
			if (!localId) {
				return undefined;
			}
			return { type: 'removeNode', localId };
		case 'removeNodes': {
			const localIds = Array.isArray(args.localIds)
				? (args.localIds as unknown[]).filter((id): id is string => typeof id === 'string')
				: [];
			if (localIds.length === 0) {
				return undefined;
			}
			return { type: 'removeNodes', localIds };
		}
		case 'moveNode': {
			const anchorLocalId = typeof args.anchorLocalId === 'string' ? args.anchorLocalId : '';
			const relationship =
				args.relationship === 'before' || args.relationship === 'after'
					? args.relationship
					: undefined;
			if (!localId || !anchorLocalId || !relationship) {
				return undefined;
			}
			return { type: 'moveNode', localId, anchorLocalId, relationship };
		}
		case 'wrap':
		case 'wrapNode': {
			const wrapperType = typeof args.wrapperType === 'string' ? args.wrapperType : '';
			const wrapperAttrs =
				args.wrapperAttrs &&
				typeof args.wrapperAttrs === 'object' &&
				!Array.isArray(args.wrapperAttrs)
					? (args.wrapperAttrs as Record<string, unknown>)
					: undefined;
			if (!localId || !wrapperType || !wrapperAttrs) {
				return undefined;
			}
			return { type: envelope.name, localId, wrapperType, wrapperAttrs };
		}
		case 'setAttrs': {
			const attrs =
				args.attrs && typeof args.attrs === 'object' && !Array.isArray(args.attrs)
					? (args.attrs as Record<string, unknown>)
					: undefined;
			if (!localId || !attrs) {
				return undefined;
			}
			return { type: 'setAttrs', localId, attrs };
		}
		default:
			return undefined;
	}
}
